import type { CompanyIdType } from "@biteasy/contracts";
import {
	CompanyIdTypeSchema,
	withCompanyIdValidation,
	withConditionalGroups,
} from "@biteasy/contracts";
import { zodResolver } from "@hookform/resolvers/zod";
import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { authClient, useAuth } from "@/auth";
import { Button } from "@/components/catalyst/Button";
import {
	Combobox,
	ComboboxLabel,
	ComboboxOption,
} from "@/components/catalyst/Combobox";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import {
	ErrorMessage,
	Field,
	FieldGroup,
	Label,
} from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import { orpc } from "@/orpc/client";
import type { Country } from "@/util/countries";
import { COUNTRIES } from "@/util/countries";

const COMPANY_ID_TYPES: { value: CompanyIdType; label: string }[] = [
	{ value: "bn", label: "Business Number (Canada)" },
	{ value: "gst", label: "GST Number" },
	{ value: "ein", label: "EIN (US)" },
	{ value: "vat", label: "VAT Number (EU/UK)" },
	{ value: "abn", label: "ABN (Australia)" },
	{ value: "other", label: "Other" },
];

/** Maps Stripe tax ID types back to our CompanyIdType */
function mapFromStripeTaxIdType(stripeType: string): CompanyIdType | undefined {
	const mapping: Record<string, CompanyIdType> = {
		us_ein: "ein",
		eu_vat: "vat",
		gb_vat: "vat",
		ca_bn: "bn",
		ca_gst_hst: "gst",
		au_abn: "abn",
	};
	return mapping[stripeType];
}

const profileFormSchema = withCompanyIdValidation(
	withConditionalGroups(
		z.object({
			name: z.string().min(1, "Name is required"),
			country: z.string().min(1, "Country is required"),
			companyName: z.string(),
			companyIdType: CompanyIdTypeSchema,
			companyIdValue: z.string(),
		}),
		[["companyName", "companyIdType", "companyIdValue"]],
		{
			companyName: "Please provide your company name",
			companyIdType: "Please select an identifier type",
			companyIdValue: "Please provide your company identifier",
		},
	),
);

type ProfileFormData = z.infer<typeof profileFormSchema>;
type ProfileFormInput = z.input<typeof profileFormSchema>;

export function ProfileModal({
	isOpen,
	onClose,
}: {
	isOpen: boolean;
	onClose: () => void;
}) {
	const session = useAuth();
	const router = useRouter();
	const { refetch } = authClient.useSession();

	// Fetch billing details to get company info from Stripe
	const { data: billingDetails } = useQuery(
		orpc.billing.getBillingDetails.queryOptions({
			input: isOpen ? {} : skipToken,
		}),
	);

	const {
		register,
		handleSubmit,
		resetField,
		control,
		formState: { errors },
	} = useForm<ProfileFormInput>({
		resolver: zodResolver(profileFormSchema),
		defaultValues: {
			name: "",
			country: "",
			companyName: undefined,
			companyIdType: undefined,
			companyIdValue: undefined,
		},
	});

	// Populate name from user session
	useEffect(() => {
		if (isOpen && session?.user?.name) {
			resetField("name", { defaultValue: session.user.name });
		}
	}, [isOpen, session?.user?.name, resetField]);

	// Populate billing fields from Stripe
	useEffect(() => {
		if (isOpen && billingDetails) {
			const taxId = billingDetails.taxIds?.[0];
			resetField("country", {
				defaultValue: billingDetails.billingAddress?.country ?? "",
			});
			resetField("companyName", {
				defaultValue: billingDetails.companyName ?? undefined,
			});
			resetField("companyIdType", {
				defaultValue: taxId ? mapFromStripeTaxIdType(taxId.type) : undefined,
			});
			resetField("companyIdValue", {
				defaultValue: taxId?.value ?? undefined,
			});
		}
	}, [isOpen, billingDetails, resetField]);

	const { mutate: updateCompanyInfo } = useMutation(
		orpc.billing.updateCompanyInfo.mutationOptions(),
	);

	const { mutate: updateProfile, isPending } = useMutation({
		mutationFn: async (data: ProfileFormData) => {
			authClient.updateUser({
				name: data.name,
			});
			updateCompanyInfo({
				name: data.name,
				country: data.country,
				companyName: data.companyName,
				companyIdType: data.companyIdType,
				companyIdValue: data.companyIdValue,
			});
		},
		onSuccess: () => {
			toast.success("Profile updated");
			router.invalidate();
			refetch();
			onClose();
		},
		onError: (error) => {
			toast.error("Failed to update profile");
			console.error(error);
		},
	});

	const onSubmit = (data: ProfileFormData) => {
		updateProfile(data);
	};

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogTitle>Profile</DialogTitle>
			<DialogDescription>Update your personal information.</DialogDescription>
			<DialogBody>
				<form
					id="profile-form"
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col gap-4"
				>
					<FieldGroup>
						<Field>
							<Label>Name</Label>
							<Input {...register("name")} />
							{errors.name?.message && (
								<ErrorMessage>{errors.name.message}</ErrorMessage>
							)}
						</Field>
						<Field>
							<Label>Country</Label>
							<Controller
								name="country"
								control={control}
								render={({ field }) => (
									<Combobox
										options={COUNTRIES}
										value={COUNTRIES.find((c) => c.code === field.value)}
										onChange={(country: Country | null) =>
											field.onChange(country?.code ?? "")
										}
										displayValue={(country) => country?.name}
										placeholder="Select a country"
										aria-label="Country"
									>
										{(country) => (
											<ComboboxOption value={country}>
												<ComboboxLabel>{country.name}</ComboboxLabel>
											</ComboboxOption>
										)}
									</Combobox>
								)}
							/>
							{errors.country?.message && (
								<ErrorMessage>{errors.country.message}</ErrorMessage>
							)}
						</Field>
						<Field>
							<Label>Company Name (Optional)</Label>
							<Input
								type="text"
								{...register("companyName")}
								placeholder="Company name"
							/>
							{errors.companyName?.message && (
								<ErrorMessage>{errors.companyName.message}</ErrorMessage>
							)}
						</Field>
						<div className="grid grid-cols-2 gap-4">
							<Field>
								<Label>Company ID</Label>
								<Select {...register("companyIdType")}>
									<option value="">Select type...</option>
									{COMPANY_ID_TYPES.map((type) => (
										<option key={type.value} value={type.value}>
											{type.label}
										</option>
									))}
								</Select>
								{errors.companyIdType?.message && (
									<ErrorMessage>{errors.companyIdType.message}</ErrorMessage>
								)}
							</Field>
							<Field>
								<Label>Number</Label>
								<Input
									type="text"
									{...register("companyIdValue")}
									placeholder="Identifier"
								/>
								{errors.companyIdValue?.message && (
									<ErrorMessage>{errors.companyIdValue.message}</ErrorMessage>
								)}
							</Field>
						</div>
					</FieldGroup>
				</form>
			</DialogBody>
			<DialogActions>
				<Button plain onClick={onClose}>
					Cancel
				</Button>
				<Button type="submit" form="profile-form" disabled={isPending}>
					{isPending ? "Saving..." : "Save changes"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
